import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Mic, MicOff, Volume2, CheckCircle, Loader2, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const VOICE_PROMPTS = [
  { key: "overall_feeling", question: "On a scale of 1 to 5, how are you feeling overall today?", type: "number", min: 1, max: 5 },
  { key: "pain_level", question: "On a scale of 0 to 10, what is your pain level?", type: "number", min: 0, max: 10 },
  { key: "sleep_quality", question: "How was your sleep last night? Say 1 for poor, 5 for excellent.", type: "number", min: 1, max: 5 },
  { key: "appetite", question: "How is your appetite? 1 for no appetite, 5 for good appetite.", type: "number", min: 1, max: 5 },
  { key: "mood", question: "How would you describe your mood? You can say peaceful, happy, okay, tired, anxious, or sad.", type: "mood" },
  { key: "symptoms", question: "Are you experiencing any symptoms? You can say things like pain, nausea, fatigue, or say none.", type: "symptoms" },
  { key: "notes", question: "Is there anything else you would like to add? Say skip if nothing.", type: "text" }
];

const MOOD_KEYWORDS = {
  "peaceful": "peaceful", "peace": "peaceful", "calm": "peaceful",
  "happy": "happy", "good": "happy", "great": "happy", "wonderful": "happy",
  "okay": "okay", "ok": "okay", "fine": "okay", "alright": "okay",
  "tired": "tired", "exhausted": "tired", "sleepy": "tired",
  "anxious": "anxious", "worried": "anxious", "nervous": "anxious",
  "sad": "sad", "down": "sad", "depressed": "sad", "low": "sad"
};

const SYMPTOM_KEYWORDS = [
  "pain", "nausea", "fatigue", "shortness of breath", "constipation",
  "anxiety", "confusion", "loss of appetite", "insomnia", "weakness",
  "headache", "dizziness", "vomiting"
];

export default function VoiceCheckin({ open, onOpenChange, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({
    overall_feeling: 3,
    pain_level: 0,
    sleep_quality: 3,
    appetite: 3,
    mobility: 3,
    mood: "okay",
    symptoms: [],
    notes: ""
  });
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [recognition, setRecognition] = useState(null);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognizer = new SpeechRecognition();
        recognizer.continuous = false;
        recognizer.interimResults = true;
        recognizer.lang = 'en-US';
        
        recognizer.onresult = (event) => {
          const current = event.resultIndex;
          const result = event.results[current];
          const text = result[0].transcript.toLowerCase();
          setTranscript(text);
          
          if (result.isFinal) {
            processVoiceInput(text);
          }
        };
        
        recognizer.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
          if (event.error === 'not-allowed') {
            setError("Microphone access denied. Please enable microphone permissions.");
          }
        };
        
        recognizer.onend = () => {
          setIsListening(false);
        };
        
        setRecognition(recognizer);
      } else {
        setError("Speech recognition is not supported in this browser.");
      }
    }
  }, []);

  // Speak the current prompt when step changes
  useEffect(() => {
    if (open && currentStep < VOICE_PROMPTS.length) {
      speakPrompt(VOICE_PROMPTS[currentStep].question);
    }
  }, [currentStep, open]);

  const speakPrompt = useCallback((text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(true);
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.volume = 1;
      
      // Try to use a friendly voice
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(v => 
        v.name.includes('Samantha') || 
        v.name.includes('Karen') || 
        v.name.includes('Google US English Female')
      );
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
      
      utterance.onend = () => {
        setIsSpeaking(false);
        // Auto-start listening after speaking
        setTimeout(() => startListening(), 500);
      };
      
      window.speechSynthesis.speak(utterance);
    }
  }, []);

  const startListening = () => {
    if (recognition && !isListening) {
      setTranscript("");
      setIsListening(true);
      try {
        recognition.start();
      } catch (e) {
        console.error("Recognition start error:", e);
      }
    }
  };

  const stopListening = () => {
    if (recognition && isListening) {
      recognition.stop();
      setIsListening(false);
    }
  };

  const processVoiceInput = (text) => {
    const prompt = VOICE_PROMPTS[currentStep];
    let value = null;
    
    if (prompt.type === "number") {
      // Extract number from speech
      const numbers = text.match(/\d+/);
      if (numbers) {
        const num = parseInt(numbers[0]);
        if (num >= prompt.min && num <= prompt.max) {
          value = num;
        }
      }
      // Handle word numbers
      const wordNumbers = { "one": 1, "two": 2, "three": 3, "four": 4, "five": 5, 
                           "six": 6, "seven": 7, "eight": 8, "nine": 9, "ten": 10, "zero": 0 };
      for (const [word, num] of Object.entries(wordNumbers)) {
        if (text.includes(word) && num >= prompt.min && num <= prompt.max) {
          value = num;
          break;
        }
      }
    } else if (prompt.type === "mood") {
      // Find mood keyword
      for (const [keyword, mood] of Object.entries(MOOD_KEYWORDS)) {
        if (text.includes(keyword)) {
          value = mood;
          break;
        }
      }
    } else if (prompt.type === "symptoms") {
      if (text.includes("none") || text.includes("no symptoms") || text.includes("nothing")) {
        value = [];
      } else {
        // Find symptom keywords
        const foundSymptoms = SYMPTOM_KEYWORDS.filter(s => text.includes(s.toLowerCase()));
        if (foundSymptoms.length > 0) {
          value = foundSymptoms.map(s => s.charAt(0).toUpperCase() + s.slice(1));
        }
      }
    } else if (prompt.type === "text") {
      if (text.includes("skip") || text.includes("nothing") || text.includes("no")) {
        value = "";
      } else {
        value = text;
      }
    }
    
    if (value !== null) {
      setResponses(prev => ({ ...prev, [prompt.key]: value }));
      
      // Confirm and move to next
      const confirmMsg = getConfirmationMessage(prompt.key, value);
      speakConfirmation(confirmMsg);
    } else {
      // Didn't understand, ask again
      speakPrompt("I didn't quite catch that. " + prompt.question);
    }
  };

  const getConfirmationMessage = (key, value) => {
    switch (key) {
      case "overall_feeling":
        return `You said ${value} out of 5.`;
      case "pain_level":
        return `Pain level ${value} out of 10.`;
      case "sleep_quality":
        return `Sleep quality ${value} out of 5.`;
      case "appetite":
        return `Appetite ${value} out of 5.`;
      case "mood":
        return `Feeling ${value}.`;
      case "symptoms":
        return value.length > 0 ? `Noted: ${value.join(", ")}.` : "No symptoms noted.";
      case "notes":
        return value ? "Note recorded." : "Skipped.";
      default:
        return "Got it.";
    }
  };

  const speakConfirmation = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(true);
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1;
      
      utterance.onend = () => {
        setIsSpeaking(false);
        // Move to next step
        if (currentStep < VOICE_PROMPTS.length - 1) {
          setCurrentStep(prev => prev + 1);
        } else {
          // All done, submit
          handleSubmit();
        }
      };
      
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await api.post("/checkin", responses);
      
      // Speak completion message
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(
          "Your check-in is complete. Thank you for taking time to track your wellness today. Take care."
        );
        utterance.rate = 0.9;
        window.speechSynthesis.speak(utterance);
      }
      
      toast.success("Voice check-in completed!");
      onComplete?.();
      onOpenChange(false);
      resetCheckin();
    } catch (error) {
      toast.error("Failed to save check-in");
      setError("Failed to save. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetCheckin = () => {
    setCurrentStep(0);
    setTranscript("");
    setResponses({
      overall_feeling: 3,
      pain_level: 0,
      sleep_quality: 3,
      appetite: 3,
      mobility: 3,
      mood: "okay",
      symptoms: [],
      notes: ""
    });
  };

  const handleClose = () => {
    stopListening();
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    onOpenChange(false);
    resetCheckin();
  };

  const currentPrompt = VOICE_PROMPTS[currentStep];
  const progress = ((currentStep + 1) / VOICE_PROMPTS.length) * 100;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="voice-checkin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Volume2 className="w-5 h-5 text-primary" />
            Voice Check-in
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Step {currentStep + 1} of {VOICE_PROMPTS.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Current Question */}
          <div className="text-center py-6">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 transition-all ${
              isSpeaking ? 'bg-blue-100 animate-pulse' : isListening ? 'bg-green-100' : 'bg-muted'
            }`}>
              {isSpeaking ? (
                <Volume2 className="w-10 h-10 text-blue-600 animate-pulse" />
              ) : isListening ? (
                <Mic className="w-10 h-10 text-green-600 animate-pulse" />
              ) : (
                <Mic className="w-10 h-10 text-muted-foreground" />
              )}
            </div>
            
            <p className="text-lg font-medium mb-2">
              {currentPrompt?.question}
            </p>
            
            {/* Status */}
            <Badge variant={isSpeaking ? "default" : isListening ? "secondary" : "outline"} className="mb-4">
              {isSpeaking ? "Speaking..." : isListening ? "Listening..." : "Tap to speak"}
            </Badge>

            {/* Transcript */}
            {transcript && (
              <div className="p-3 bg-muted rounded-lg mt-4">
                <p className="text-sm text-muted-foreground">You said:</p>
                <p className="font-medium">{transcript}</p>
              </div>
            )}
          </div>

          {/* Current Responses Summary */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            {Object.entries(responses).map(([key, value]) => {
              const stepIndex = VOICE_PROMPTS.findIndex(p => p.key === key);
              const isCompleted = stepIndex < currentStep;
              const isCurrent = stepIndex === currentStep;
              
              if (key === "mobility") return null;
              
              return (
                <div 
                  key={key}
                  className={`p-2 rounded border ${
                    isCompleted ? 'bg-green-50 border-green-200' : 
                    isCurrent ? 'bg-blue-50 border-blue-200' : 'bg-muted/50 border-muted'
                  }`}
                >
                  <span className="text-xs text-muted-foreground capitalize">{key.replace("_", " ")}</span>
                  <p className="font-medium truncate">
                    {Array.isArray(value) ? (value.length > 0 ? value.join(", ") : "None") : value || "-"}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Controls */}
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => {
                stopListening();
                if ('speechSynthesis' in window) window.speechSynthesis.cancel();
                speakPrompt(currentPrompt?.question);
              }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Repeat
            </Button>
            
            <Button 
              className="flex-1"
              onClick={isListening ? stopListening : startListening}
              disabled={isSpeaking || isSubmitting}
              data-testid="voice-toggle-btn"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : isListening ? (
                <MicOff className="w-4 h-4 mr-2" />
              ) : (
                <Mic className="w-4 h-4 mr-2" />
              )}
              {isListening ? "Stop" : "Speak"}
            </Button>
          </div>

          {/* Skip to manual */}
          <p className="text-center text-xs text-muted-foreground">
            Having trouble? You can also complete the{" "}
            <button 
              className="text-primary underline"
              onClick={handleClose}
            >
              manual check-in
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
